import { type ContentItem as ContentItemType } from '@/common/types/lessonPlan';
import { ArrowRightIcon } from '@phosphor-icons/react';
import styled from 'styled-components';
import { formatSoundRecognitionValue } from './utils';

interface ContentItemProps {
  item: ContentItemType;
  onClick: () => void;
}

export default function ContentItem({ item, onClick }: ContentItemProps) {
  if (typeof item.value === 'string') {
    const display = item.value.includes('|')
      ? formatSoundRecognitionValue(item.value)
      : item.value;
    return (
      <ContentItemContainer onClick={onClick}>
        <ContentItemValue>{display}</ContentItemValue>
      </ContentItemContainer>
    );
  } else {
    return (
      <SoundSwappingContainer onClick={onClick}>
        {item.value.map((value, index) => (
          <div key={index} className="flex items-center">
            <ContentItemContainer>
              <ContentItemValue>{value}</ContentItemValue>
            </ContentItemContainer>
            {index < item.value.length - 1 && (
              <ArrowContainer>
                <ArrowRightIcon size={16} weight="bold" />
              </ArrowContainer>
            )}
          </div>
        ))}
      </SoundSwappingContainer>
    );
  }
}

const ContentItemContainer = styled.div`
  position: relative;
  border: 1px solid hsl(var(--border));
  border-radius: calc(var(--radius) * 0.5);
  padding: 8px 12px;
  background-color: hsl(var(--background));
  text-align: center;
  min-width: 15px;
  min-height: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 13px;
  color: hsl(var(--foreground));
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

  &:hover {
    background-color: hsl(var(--accent));
    transform: translateY(-1px);
    box-shadow:
      0 3px 8px -1px rgb(0 0 0 / 0.1),
      0 1px 4px -2px rgb(0 0 0 / 0.1);
  }

  &:active {
    transform: translateY(0px);
  }
`;

const ContentItemValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  line-height: 1.2;

  strong {
    font-weight: 500;
    color: hsl(var(--primary));
  }
`;

const SoundSwappingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px;
  border-radius: calc(var(--radius) * 0.75);
  background-color: hsl(var(--muted) / 0.3);

  &:hover {
    background-color: hsl(var(--muted) / 0.5);
  }
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 4px;
  color: hsl(var(--muted-foreground));
`;
